import type { DineroDeAnalisis } from '../lib/analisis.js';
import type { FiadoDelPeriodo, LeToca, RiesgoDeFiado } from '../lib/clientela.js';
import { pasoSuLimite } from '../lib/porCobrar.js';
import { useT } from '../i18n/index.js';
import { ACliente, Corta, Fila, Nada, Seccion, pct } from './PiezasDeAnalisis.js';

/**
 * What «Clientes» in «Análisis» says besides who owes and who stopped coming (the owner, 2026-10-13:
 * «let's build all of it»): how the fiado goes, in the period and client by client, and the regulars
 * whose day to buy has come. The arithmetic is `lib/clientela.ts`.
 */

/**
 * «Cómo va el fiado»: what was given on credit, collected and written off in the period, and what
 * selling on credit left; then each client who owes, against a month ago, their limit, and how
 * long they take to pay.
 */
export function ComoVaElFiado({ periodo, riesgos, dinero }: { periodo: FiadoDelPeriodo; riesgos: readonly RiesgoDeFiado[]; dinero: DineroDeAnalisis }) {
  const t = useT();
  const movido = periodo.dado > 0 || periodo.cobrado > 0 || periodo.perdido > 0;
  const cambio = (r: RiesgoDeFiado) => {
    const d = r.debe - r.antes;
    return d > 0 ? t('analisis.fiadoVa.subio', { monto: dinero(d) }) : d < 0 ? t('analisis.fiadoVa.bajo', { monto: dinero(-d) }) : t('analisis.fiadoVa.igual');
  };
  return (
    <Seccion titulo={t('analisis.fiadoVa.titulo')} ayuda={t('analisis.fiadoVa.ayuda')} datos="fiado-va">
      {!movido && riesgos.length === 0 ? (
        <Nada texto={t('analisis.fiadoVa.nada')} />
      ) : (
        <>
          {movido && (
            <div className="flex flex-col gap-1 rounded-lg bg-slate-800 px-3 py-2 text-sm text-slate-300">
              <p data-fiado-periodo>{t('analisis.fiadoVa.periodo', { dado: dinero(periodo.dado), cobrado: dinero(periodo.cobrado), perdido: dinero(periodo.perdido) })}</p>
              <p className={`font-medium ${periodo.queda < 0 ? 'text-rose-400' : 'text-emerald-300'}`} data-fiado-queda>
                {t('analisis.fiadoVa.queda', { utilidad: dinero(periodo.utilidad), queda: dinero(periodo.queda) })}
              </p>
            </div>
          )}
          {riesgos.length > 0 && (
            <>
              <h3 className="mt-5 text-sm font-semibold text-slate-300">{t('analisis.fiadoVa.deben')}</h3>
              <Corta filas={riesgos}>
                {(vistas) => (
                  <ul className="divide-y divide-slate-800">
                    {vistas.map((r) => (
                      <Fila
                        key={r.clienteId}
                        nombre={r.clienteId}
                        a={<ACliente id={r.clienteId} nombre={r.nombre} />}
                        detalle={[
                          cambio(r),
                          r.usoBp !== null ? t('analisis.fiadoVa.limite', { pct: pct(r.usoBp) }) : null,
                          r.diasParaPagar !== null ? t('analisis.fiadoVa.paga', { n: r.diasParaPagar }) : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                        valor={dinero(r.debe)}
                        rojo={pasoSuLimite({ saldo: r.debe, limite: r.limite })}
                      />
                    ))}
                  </ul>
                )}
              </Corta>
            </>
          )}
        </>
      )}
    </Seccion>
  );
}

/** «Les toca comprar»: the regulars whose day has come, each by their own rhythm. */
export function LesTocaComprar({ lista, nombres, dinero }: { lista: readonly LeToca[]; nombres: ReadonlyMap<string, string>; dinero: DineroDeAnalisis }) {
  const t = useT();
  return (
    <Seccion titulo={t('analisis.toca.titulo')} ayuda={t('analisis.toca.ayuda')} datos="les-toca">
      {lista.length === 0 ? (
        <Nada texto={t('analisis.toca.nada')} />
      ) : (
        <Corta filas={lista}>
          {(vistas) => (
            <ul className="-my-2.5 divide-y divide-slate-800">
              {vistas.map((x) => (
                <Fila
                  key={x.clienteId}
                  nombre={x.clienteId}
                  a={<ACliente id={x.clienteId} nombre={nombres.get(x.clienteId) ?? '—'} />}
                  detalle={t('analisis.toca.detalle', { n: x.cadencia, m: x.desde })}
                  valor={t('analisis.toca.porVez', { monto: dinero(x.porVez) })}
                />
              ))}
            </ul>
          )}
        </Corta>
      )}
    </Seccion>
  );
}
